import { ShoppingCartOutlined, EditOutlined } from "@ant-design/icons"
import { Col, Divider, Row, Typography, Table, Spin, Segmented, Space, Tooltip, Button } from "antd"
import React, { useEffect, useState } from "react"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../firebase/config"
import { generateFilter } from "../../components/Orders/JobFilters"
import { ASAPOrder, OrderAndPrefix } from "../../components/Orders/Helpers/OrderAndPrefix"
import StopRequestHandler from "../../components/Orders/StopRequestHandler"
import OrderEditDrawer from "../../components/Orders/OrderEditDrawer"
import { useAuthContext } from "../../hooks/useAuthContext"
import { doc, getDoc } from "firebase/firestore"
import MissingPaperSummary from "../Manager/Orders/Helper/MissingPaperSummary"

const { Paragraph, Title, Text } = Typography

const SEGMENT_FILTERS = {
  "Pre-Payment": { status: 0.5 },
  "Designer": { status: 1 },
  "Paper-Manager": { status: 2 },
  "Arsoma": { status: 3 },
  "Tape": { status: 3.5 },
  "Viva": { status: 4 },
  "Winder": { status: 5 },
  "Warehouse": { status: 6 },
  "Completed": { status: 7 },
  "Stopped": { status: 999 },
}

export default function ProductionOrders() {
  const { user } = useAuthContext()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState("Paper-Manager")
  const [stopRequestModalOpen, setStopRequestModalOpen] = useState(false)
  const [selectedStopOrder, setSelectedStopOrder] = useState(null)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Check if user is admin
  useEffect(() => {
    if (!user) return

    const checkAdminRole = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid)
        const userDocSnap = await getDoc(userDocRef)
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data()
          setIsAdmin(userData.roles?.admin || false)
        }
      } catch (error) {
        console.error("Error checking admin role:", error)
      }
    }

    checkAdminRole()
  }, [user])

  // Fetch orders with local role-based filtering
  useEffect(() => {
    if (!user) return

    setLoading(true)
    const ref = collection(db, "orders")
    const filter = SEGMENT_FILTERS[selectedSegment]

    // Only query by orderStatus (no additional where clauses to avoid index creation)
    const q = query(ref, where("orderStatus", "==", filter.status))

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let results = snapshot.docs.map((doc) => ({
        ...doc.data(),
        key: doc.id,
        id: doc.id,
      }))

      // Filter locally: if not admin, hide orders from specific uid
      if (!isAdmin) {
        results = results.filter((order) => order.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362")
      }

      setData(results)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [selectedSegment, isAdmin, user])

  // Generate filters
  let clientFilters = generateFilter(data, ["client", "client"])
  let productFilter = generateFilter(data, ["selectedProduct", "product", "productName"])
  let machineFilter = generateFilter(data, ["selectedProduct", "machine"])

  // Extract manufacturer company names for filter
  const manufacturerNames = data
    .map((order) => {
      const mfg = order.manufacturer
      return typeof mfg === "object" ? mfg?.company : mfg
    })
    .filter(Boolean)
  const uniqueManufacturers = [...new Set(manufacturerNames)]
  let manufacturerFilter = uniqueManufacturers.map((name) => ({ text: name, value: name }))

  const handleStopClick = (record) => {
    setSelectedStopOrder(record)
    setStopRequestModalOpen(true)
  }

  const handleEditClick = (record) => {
    setSelectedOrderForEdit(record)
    setEditDrawerOpen(true)
  }

  const columns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => (
        <Space size={4}>
          {ASAPOrder(record)}
          {OrderAndPrefix(record, record.requestedToStop ? handleStopClick : null)}
        </Space>
      ),
      key: "orderNumber",
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      filters: clientFilters,
      onFilter: (value, record) => record.client?.client?.startsWith(value),
      filterSearch: true,
      width: "12%",
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
      key: "client",
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilter,
      onFilter: (value, record) => record.selectedProduct?.product?.productName?.startsWith(value),
      filterSearch: true,
      width: "15%",
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
      key: "product",
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      filters: machineFilter,
      onFilter: (value, record) => record.selectedProduct?.machine?.startsWith(value),
      filterSearch: true,
      width: "10%",
      key: "machine",
    },
    {
      key: "plateLocation",
      title: "Plate Location",
      dataIndex: ["selectedPlate", "plateLocation"],
      width: 130,
      align: "center",
      render: (value, record) =>
        typeof record.selectedPlate === "object" && record.selectedPlate !== null ? (
          value
        ) : (
          <span style={{ opacity: 0.4 }}>—</span>
        ),
    },
    {
      title: "Manufacturer",
      dataIndex: "manufacturer",
      align: "center",
      filters: manufacturerFilter,
      onFilter: (value, record) => {
        const mfg = record.manufacturer
        const mfgName = typeof mfg === "object" ? mfg?.company : mfg
        return mfgName?.startsWith(value)
      },
      filterSearch: true,
      width: "10%",
      key: "manufacturer",
      render: (mfg) => {
        const mfgName = typeof mfg === "object" ? mfg?.company : mfg
        return (
          <Tooltip title={mfgName}>
            <Text ellipsis>{mfgName || "N/A"}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "8%",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditClick(record)}>
          Edit
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Title level={3}>
            <ShoppingCartOutlined />
            <Divider type="vertical" />
            Production Orders
          </Title>
        </Col>
        <Col span={24}>
          <Paragraph type="secondary">
            Overview of all active production orders across all machines and statuses.
          </Paragraph>
        </Col>
        <Col span={24}>
          <Segmented
            value={selectedSegment}
            onChange={setSelectedSegment}
            options={Object.keys(SEGMENT_FILTERS)}
            block
          />
        </Col>

        {/* View Required Materials button for Designer and Paper-Manager tabs */}
        {(selectedSegment === "Designer" || selectedSegment === "Paper-Manager") && (
          <Col span={24}>
            <MissingPaperSummary
              data={data}
              loading={loading}
              filterStatus={SEGMENT_FILTERS[selectedSegment].status}
            />
          </Col>
        )}
      </Row>

      <Spin spinning={loading}>
        <Table
          style={{ marginTop: "15px" }}
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey={(record) => record.id}
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          scroll={{ x: "max-content" }}
        />
      </Spin>

      {selectedOrderForEdit && (
        <OrderEditDrawer
          order={selectedOrderForEdit}
          isOpen={editDrawerOpen}
          onClose={() => {
            setEditDrawerOpen(false)
            setSelectedOrderForEdit(null)
          }}
        />
      )}

      {selectedStopOrder && (
        <StopRequestHandler
          order={selectedStopOrder}
          isOpen={stopRequestModalOpen}
          onClose={() => setStopRequestModalOpen(false)}
        />
      )}
    </div>
  )
}
